import api from '.';
import {
  OrganiseCalendar,
  OrganiseEntry,
  OrganiseSeries,
  OrganiseCareCircleEntries,
  CreateOrganiseEntryRequest,
  UpdateOrganiseEntryRequest,
  UpdateOrganiseEntryStatusRequest,
  CreateOrganiseSeriesRequest,
  UpdateOrganiseSeriesRequest,
  CreateOrganiseExceptionRequest,
} from '@karehero/models';

export const organiseApi = api.injectEndpoints({
  endpoints: (builder) => ({
    listAllOrganiseEntries: builder.query<OrganiseCareCircleEntries[], void>({
      query: () => '/organise/entries',
      providesTags: (result) => [
        { type: 'Organise', id: 'LIST' },
        ...(result ?? []).map((g) => ({
          type: 'Organise' as const,
          id: g.calendar.id,
        })),
      ],
    }),
    getCalendarForCareCircle: builder.query<OrganiseCalendar, string>({
      query: (careCircleId) => `/organise/care-circle/${careCircleId}/calendar`,
      providesTags: (_result, _error, careCircleId) => [
        { type: 'Organise', id: careCircleId },
      ],
    }),
    listOrganiseEntries: builder.query<
      OrganiseEntry[],
      { calendarId: string; from?: string; to?: string }
    >({
      query: ({ calendarId, from, to }) => {
        const params = new URLSearchParams();
        if (from) params.set('from', from);
        if (to) params.set('to', to);
        const qs = params.toString();
        return `/organise/${calendarId}${qs ? `?${qs}` : ''}`;
      },
      providesTags: (_result, _error, { calendarId }) => [
        { type: 'Organise', id: calendarId },
      ],
    }),
    createOrganiseEntry: builder.mutation<
      OrganiseEntry,
      { calendarId: string; body: CreateOrganiseEntryRequest }
    >({
      query: ({ calendarId, body }) => ({
        url: `/organise/${calendarId}/entry`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { calendarId }) => [
        { type: 'Organise', id: calendarId },
      ],
    }),
    updateOrganiseEntry: builder.mutation<
      OrganiseEntry,
      {
        calendarId: string;
        entryId: string;
        body: UpdateOrganiseEntryRequest;
      }
    >({
      query: ({ calendarId, entryId, body }) => ({
        url: `/organise/${calendarId}/entry/${entryId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, { calendarId }) => [
        { type: 'Organise', id: calendarId },
      ],
    }),
    updateOrganiseEntryStatus: builder.mutation<
      OrganiseEntry,
      {
        calendarId: string;
        entryId: string;
        body: UpdateOrganiseEntryStatusRequest;
      }
    >({
      query: ({ calendarId, entryId, body }) => ({
        url: `/organise/${calendarId}/entry/${entryId}/status`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { calendarId }) => [
        { type: 'Organise', id: calendarId },
      ],
    }),
    deleteOrganiseEntry: builder.mutation<
      void,
      { calendarId: string; entryId: string }
    >({
      query: ({ calendarId, entryId }) => ({
        url: `/organise/${calendarId}/entry/${entryId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { calendarId }) => [
        { type: 'Organise', id: calendarId },
      ],
    }),

    // --- Series (recurring events) ---

    createOrganiseSeries: builder.mutation<
      OrganiseSeries,
      { calendarId: string; body: CreateOrganiseSeriesRequest }
    >({
      query: ({ calendarId, body }) => ({
        url: `/organise/${calendarId}/series`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { calendarId }) => [
        { type: 'Organise', id: calendarId },
      ],
    }),
    updateOrganiseSeries: builder.mutation<
      OrganiseSeries,
      {
        calendarId: string;
        seriesId: string;
        body: UpdateOrganiseSeriesRequest;
      }
    >({
      query: ({ calendarId, seriesId, body }) => ({
        url: `/organise/${calendarId}/series/${seriesId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, { calendarId }) => [
        { type: 'Organise', id: calendarId },
      ],
    }),
    deleteOrganiseSeries: builder.mutation<
      void,
      { calendarId: string; seriesId: string }
    >({
      query: ({ calendarId, seriesId }) => ({
        url: `/organise/${calendarId}/series/${seriesId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { calendarId }) => [
        { type: 'Organise', id: calendarId },
      ],
    }),
    createOrganiseException: builder.mutation<
      void,
      {
        calendarId: string;
        seriesId: string;
        body: CreateOrganiseExceptionRequest;
      }
    >({
      query: ({ calendarId, seriesId, body }) => ({
        url: `/organise/${calendarId}/series/${seriesId}/exception`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { calendarId }) => [
        { type: 'Organise', id: calendarId },
      ],
    }),
  }),
});

export const {
  useListAllOrganiseEntriesQuery,
  useGetCalendarForCareCircleQuery,
  useListOrganiseEntriesQuery,
  useCreateOrganiseEntryMutation,
  useUpdateOrganiseEntryMutation,
  useUpdateOrganiseEntryStatusMutation,
  useDeleteOrganiseEntryMutation,
  useCreateOrganiseSeriesMutation,
  useUpdateOrganiseSeriesMutation,
  useDeleteOrganiseSeriesMutation,
  useCreateOrganiseExceptionMutation,
} = organiseApi;

export const invalidateOrganiseTags = () => {
  return organiseApi.util.invalidateTags(['Organise']);
};

export default organiseApi;
